import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  Copy,
  CreditCard,
  ExternalLink,
  MapPin,
  MessageCircle,
  Package,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  Truck,
} from 'lucide-react'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { formatDateTime, formatRoomPrice, isLodgingShop } from '@/lib/format'
import { haptic, openExternalLink } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT, getT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'
import type { DeliveryTrackingStatus, LodgingBooking, Order } from '@/shared/types'
import { FopRequisitesModal } from '@/components/FopRequisitesModal'
import {
  StorefrontPage,
  StorefrontCard,
  StorefrontEmptyState,
  StorefrontLoading,
} from '@/components/StorefrontPage'

const LODGING_STATUS_LABEL: Record<string, { uk: string; en: string; ru: string; classes: string }> = {
  pending: {
    uk: 'Очікує підтвердження',
    en: 'Pending confirmation',
    ru: 'Ожидает подтверждения',
    classes: 'bg-yellow-500/10 text-yellow-600 border border-yellow-500/20',
  },
  confirmed: {
    uk: 'Підтверджено',
    en: 'Confirmed',
    ru: 'Подтверждено',
    classes: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20',
  },
  cancelled: {
    uk: 'Скасовано',
    en: 'Cancelled',
    ru: 'Отменено',
    classes: 'bg-rose-500/10 text-rose-600 border border-rose-500/20',
  },
  done: {
    uk: 'Завершено',
    en: 'Completed',
    ru: 'Завершено',
    classes: 'bg-gray-500/10 text-gray-600 border border-gray-500/20',
  },
}

function LodgingBookingsSection({
  bookings,
  lang,
}: {
  bookings: LodgingBooking[]
  lang: 'uk' | 'en' | 'ru'
}) {
  const qc = useQueryClient()
  const title = lang === 'en' ? 'My reservations' : lang === 'ru' ? 'Мои бронирования' : 'Мої бронювання'
  const stayLabel = lang === 'en' ? 'Stay' : lang === 'ru' ? 'Период проживания' : 'Період перебування'
  const cancelLabel = lang === 'en' ? 'Cancel' : lang === 'ru' ? 'Отменить' : 'Скасувати'
  const prepaidLabel =
    lang === 'en' ? 'Deposit received' : lang === 'ru' ? 'Предоплата получена' : 'Передоплата отримана'

  const cancelMutation = useMutation({
    mutationFn: (bookingId: number) => api.post(`/lodging/bookings/${bookingId}/cancel`, {}),
    onSuccess: () => {
      haptic('success')
      qc.invalidateQueries({ queryKey: ['lodging-my-bookings'] })
    },
    onError: () => haptic('error'),
  })

  return (
    <div className="mb-6">
      <h2 className="px-1 mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">{title}</h2>
      <div className="space-y-3">
        {bookings.map((b) => {
          const badge = LODGING_STATUS_LABEL[b.status] ?? LODGING_STATUS_LABEL.pending
          return (
            <StorefrontCard key={b.id} className="p-4 flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-slate-900">#{b.id}</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge.classes}`}
                  >
                    {badge[lang]}
                  </span>
                  {b.prepaid && (
                    <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                      {prepaidLabel}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500">
                {stayLabel}:{' '}
                <span className="font-medium text-slate-700">
                  {b.check_in_date} — {b.check_out_date}
                </span>
              </p>
              <div className="mt-1 flex items-center justify-between border-t border-slate-100 pt-2">
                <p className="text-sm font-bold text-brand-600">{formatRoomPrice(b.total_price_uah)}</p>
                {b.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('medium')
                      cancelMutation.mutate(b.id)
                    }}
                    disabled={cancelMutation.isPending}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50"
                  >
                    {cancelLabel}
                  </button>
                )}
              </div>
            </StorefrontCard>
          )
        })}
      </div>
    </div>
  )
}

const STATUS_ICON: Record<string, string> = {
  new: '🔴',
  seen: '👁',
  shipped: '📦',
  done: '✅',
  cancelled: '❌',
  refunded: '↩️',
}

function formatDate(iso: string, lang: string): string {
  return formatDateTime(iso, lang as 'uk' | 'en' | 'ru')
}

interface IbanPaymentDetails {
  enabled?: boolean
  order_id: number
  amount_uah: number
  paid: boolean
  bank_recipient: string
  bank_card: string
  bank_iban: string
  bank_extra_info: string
  edrpou?: string
  purpose: string
  nbu_payload: string
  qr_svg: string
}

function IbanPaymentCard({ orderId }: { orderId: number }) {
  const theme = useStoreTheme()
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showFopModal, setShowFopModal] = useState(false)
  const { data: info, isLoading } = useQuery<IbanPaymentDetails>({
    queryKey: ['iban-payment', orderId],
    queryFn: () => api.get<IbanPaymentDetails>(`/orders/${orderId}/iban-payment`),
  })

  function copy(text: string, label: string) {
    void navigator.clipboard.writeText(text)
    haptic('success')
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 2000)
  }

  if (isLoading || !info) return null
  if (!info.enabled || (!info.bank_iban && !info.bank_card)) return null

  const allRequisitesText = [
    info.bank_recipient ? `Отримувач: ${info.bank_recipient}` : '',
    info.edrpou ? `ЄДРПОУ / ІПН: ${info.edrpou}` : '',
    info.bank_iban ? `IBAN: ${info.bank_iban}` : '',
    info.bank_card ? `Картка: ${info.bank_card}` : '',
    info.amount_uah ? `Сума: ${info.amount_uah} UAH` : '',
    info.purpose ? `Призначення: ${info.purpose}` : '',
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <div
      className={`mt-3 rounded-2xl border p-4 text-xs space-y-3 ${
        theme.isDarkTheme
          ? 'border-[#3a4352] bg-[#1a202c]/90 text-slate-200'
          : 'border-indigo-100 bg-gradient-to-br from-indigo-50/50 via-white to-blue-50/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`font-bold text-sm flex items-center gap-1.5 ${
            theme.isDarkTheme ? 'text-amber-300' : 'text-indigo-900'
          }`}
        >
          🏦 Реквізити для оплати (IBAN / ФОП)
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setShowFopModal(true)
            }}
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-lg border transition ${
              theme.isDarkTheme
                ? 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
                : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            Детально / Модалка
          </button>
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              theme.isDarkTheme
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                : 'text-indigo-600 bg-indigo-100/70'
            }`}
          >
            НБУ QR
          </span>
        </div>
      </div>

      {info.qr_svg && (
        <div
          className={`flex flex-col items-center justify-center p-3 rounded-xl border ${
            theme.isDarkTheme ? 'bg-white border-[#3a4352]' : 'bg-white border-indigo-100 shadow-inner'
          }`}
        >
          <div
            className="w-44 h-44 shrink-0"
            dangerouslySetInnerHTML={{ __html: info.qr_svg }}
          />
          <p className="mt-2 text-[11px] text-slate-500 text-center font-medium">
            📱 Відскануйте в додатку банку (Monobank, Приват24) для оплати в 1 клік
          </p>
        </div>
      )}

      <div className={`space-y-2 font-mono ${theme.isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
        {info.bank_recipient && (
          <div
            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
              theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span className="text-[10px] text-slate-400 font-sans block">Отримувач</span>
              <span className={`font-semibold truncate block ${theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>
                {info.bank_recipient}
              </span>
            </div>
            <button
              type="button"
              onClick={() => copy(info.bank_recipient, 'recipient')}
              className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 ${
                theme.isDarkTheme
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {copiedField === 'recipient' ? '✓ Скопійовано' : 'Копіювати'}
            </button>
          </div>
        )}

        {info.bank_iban && (
          <div
            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
              theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span className="text-[10px] text-slate-400 font-sans block">IBAN</span>
              <span className={`font-bold truncate block ${theme.isDarkTheme ? 'text-amber-300' : 'text-indigo-950'}`}>
                {info.bank_iban}
              </span>
            </div>
            <button
              type="button"
              onClick={() => copy(info.bank_iban, 'iban')}
              className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 shadow-xs ${
                theme.isDarkTheme
                  ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d8b25c]'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {copiedField === 'iban' ? '✓ Скопійовано' : 'Копіювати IBAN'}
            </button>
          </div>
        )}

        {info.edrpou && (
          <div
            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
              theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span className="text-[10px] text-slate-400 font-sans block">ЄДРПОУ / ІПН</span>
              <span className={`font-bold truncate block ${theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>
                {info.edrpou}
              </span>
            </div>
            <button
              type="button"
              onClick={() => copy(info.edrpou || '', 'edrpou')}
              className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 ${
                theme.isDarkTheme
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {copiedField === 'edrpou' ? '✓ Скопійовано' : 'Копіювати'}
            </button>
          </div>
        )}

        {info.bank_card && (
          <div
            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
              theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
            }`}
          >
            <div>
              <span className="text-[10px] text-slate-400 font-sans block">Номер картки</span>
              <span className={`font-bold ${theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>{info.bank_card}</span>
            </div>
            <button
              type="button"
              onClick={() => copy(info.bank_card, 'card')}
              className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 ${
                theme.isDarkTheme
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {copiedField === 'card' ? '✓ Скопійовано' : 'Копіювати'}
            </button>
          </div>
        )}

        <div
          className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
            theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
          }`}
        >
          <div>
            <span className="text-[10px] text-slate-400 font-sans block">Призначення платежу</span>
            <span className={`font-semibold ${theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>{info.purpose}</span>
          </div>
          <button
            type="button"
            onClick={() => copy(info.purpose, 'purpose')}
            className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 ${
              theme.isDarkTheme
                ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            {copiedField === 'purpose' ? '✓ Скопійовано' : 'Копіювати'}
          </button>
        </div>

        {info.amount_uah && (
          <div
            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
              theme.isDarkTheme ? 'bg-[#242c38] border-[#3a4352]' : 'bg-white/80 border-slate-200/80'
            }`}
          >
            <div>
              <span className="text-[10px] text-slate-400 font-sans block">Сума до сплати</span>
              <span className="font-bold text-[#c9a24b]">{info.amount_uah} UAH</span>
            </div>
            <button
              type="button"
              onClick={() => copy(String(info.amount_uah), 'amount')}
              className={`px-2.5 py-1 text-[11px] font-sans font-semibold rounded-md transition shrink-0 ${
                theme.isDarkTheme
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {copiedField === 'amount' ? '✓ Скопійовано' : 'Копіювати'}
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => copy(allRequisitesText, 'all')}
        className={`w-full py-2.5 text-xs font-semibold rounded-xl transition flex items-center justify-center gap-1.5 shadow-sm ${
          theme.isDarkTheme
            ? 'bg-[#2d3748] hover:bg-[#3a4352] text-slate-100'
            : 'bg-slate-900 hover:bg-slate-800 text-white'
        }`}
      >
        📋 {copiedField === 'all' ? 'Усі реквізити скопійовано!' : 'Скопіювати всі реквізити в 1 клік'}
      </button>

      <FopRequisitesModal
        isOpen={showFopModal}
        onClose={() => setShowFopModal(false)}
        orderId={orderId}
        amountUah={info.amount_uah}
        purpose={info.purpose}
      />
    </div>
  )
}

function OrderTimeline({ status, lang }: { status: string; lang: 'uk' | 'en' | 'ru' }) {
  const theme = useStoreTheme()
  const T = getT(lang, 'orders')
  if (status === 'cancelled' || status === 'refunded') {
    return (
      <div
        className={`my-2.5 flex items-center gap-2 rounded-xl p-2.5 text-xs ${
          theme.isDarkTheme
            ? 'bg-rose-950/30 border border-rose-900/50 text-rose-300'
            : 'bg-rose-50 border border-rose-200 text-rose-800'
        }`}
      >
        <AlertCircle className="h-4 w-4 shrink-0 text-rose-500" />
        <span className="font-semibold">{T.status[status] ?? status}</span>
      </div>
    )
  }

  const steps = [
    { key: 'new', label: T.timeline?.new ?? 'Оформлено' },
    { key: 'seen', label: T.timeline?.seen ?? 'В обробці' },
    { key: 'shipped', label: T.timeline?.shipped ?? 'Відправлено' },
    { key: 'done', label: T.timeline?.done ?? 'Отримано' },
  ]

  const statusOrder: Record<string, number> = {
    new: 0,
    seen: 1,
    shipped: 2,
    done: 3,
  }

  const currentIdx = statusOrder[status] ?? 0

  return (
    <div className="my-3 px-1">
      <div className="flex items-center justify-between relative">
        {/* Progress Line */}
        <div
          className={`absolute top-1/2 left-4 right-4 h-0.5 -translate-y-1/2 z-0 ${
            theme.isDarkTheme ? 'bg-[#3a4352]' : 'bg-slate-200'
          }`}
        >
          <div
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${(currentIdx / (steps.length - 1)) * 100}%` }}
          />
        </div>

        {/* Step Nodes */}
        {steps.map((step, idx) => {
          const isDone = idx < currentIdx
          const isCurrent = idx === currentIdx
          return (
            <div key={step.key} className="flex flex-col items-center gap-1 z-10">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all shadow-xs ${
                  isDone
                    ? 'bg-emerald-500 text-white'
                    : isCurrent
                      ? theme.isDarkTheme
                        ? 'bg-[#c9a24b] text-[#17120f] ring-4 ring-amber-500/20 animate-pulse'
                        : 'bg-brand-600 text-white ring-4 ring-brand-100 animate-pulse'
                      : theme.isDarkTheme
                        ? 'bg-[#242c38] border-2 border-[#3a4352] text-slate-500'
                        : 'bg-white border-2 border-slate-300 text-slate-400'
                }`}
              >
                {isDone ? (
                  <Check className="h-3.5 w-3.5 stroke-[3]" />
                ) : isCurrent ? (
                  <Clock className="h-3 w-3" />
                ) : (
                  <Circle className="h-2 w-2 fill-slate-300" />
                )}
              </div>
              <span
                className={`text-[10px] text-center font-medium max-w-[65px] leading-tight ${
                  isCurrent
                    ? theme.isDarkTheme
                      ? 'text-slate-100 font-bold'
                      : 'text-slate-900 font-bold'
                    : isDone
                      ? theme.isDarkTheme
                        ? 'text-slate-300'
                        : 'text-slate-700'
                      : 'text-slate-500'
                }`}
              >
                {step.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DeliveryTrackingCard({
  orderId,
  trackingNumber,
  deliveryMethod,
  lang,
}: {
  orderId: number
  trackingNumber: string
  deliveryMethod: string
  lang: 'uk' | 'en' | 'ru'
}) {
  const theme = useStoreTheme()
  const [copied, setCopied] = useState(false)
  const T = getT(lang, 'orders')
  const { data: tracking } = useQuery<DeliveryTrackingStatus>({
    queryKey: ['order-tracking', orderId],
    queryFn: () => api.get<DeliveryTrackingStatus>(`/orders/${orderId}/tracking-status`),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const isUkrposhta = Boolean(
    (deliveryMethod && deliveryMethod.toLowerCase().includes('укрпошт')) ||
    (trackingNumber && (trackingNumber.length === 13 || /^[A-Za-z]{2}\d{9}[A-Za-z]{2}$/.test(trackingNumber.trim()))) ||
    tracking?.carrier === 'ukrposhta'
  )

  const trackingUrl =
    tracking?.tracking_url ||
    (isUkrposhta
      ? `https://track.ukrposhta.ua/tracking_UA.html?barcode=${encodeURIComponent(trackingNumber)}`
      : `https://novaposhta.ua/tracking/?cargo_number=${encodeURIComponent(trackingNumber)}`)
  const carrierName = isUkrposhta ? 'Укрпошта' : 'Нова Пошта'
  const trackingLabel = isUkrposhta ? T.trackUkrposhta : T.trackNovaPoshta

  const getCategoryBadge = (cat?: string) => {
    switch (cat) {
      case 'arrived':
        return {
          text: T.deliveryArrived,
          bg: theme.isDarkTheme
            ? 'bg-emerald-950/40 text-emerald-300 border-emerald-800'
            : 'bg-emerald-100 text-emerald-800 border-emerald-300',
        }
      case 'received':
        return {
          text: T.deliveryReceived,
          bg: theme.isDarkTheme
            ? 'bg-blue-950/40 text-blue-300 border-blue-800'
            : 'bg-blue-100 text-blue-800 border-blue-300',
        }
      case 'in_transit':
        return {
          text: T.deliveryInTransit,
          bg: theme.isDarkTheme
            ? 'bg-amber-950/40 text-amber-300 border-amber-800'
            : 'bg-amber-100 text-amber-800 border-amber-300',
        }
      default:
        return null
    }
  }

  const badge = getCategoryBadge(tracking?.status_category)

  return (
    <div
      className={`border rounded-xl p-3 my-2 text-sm space-y-2 ${
        theme.isDarkTheme
          ? 'bg-[#1a202c]/90 border-[#3a4352] text-slate-200'
          : isUkrposhta
            ? 'bg-amber-50/80 border-amber-200'
            : 'bg-red-50/80 border-red-200'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`font-semibold flex items-center gap-1.5 ${
            theme.isDarkTheme
              ? 'text-slate-100'
              : isUkrposhta
                ? 'text-amber-900'
                : 'text-red-900'
          }`}
        >
          <Package
            className={`h-4 w-4 ${
              theme.isDarkTheme
                ? 'text-amber-400'
                : isUkrposhta
                  ? 'text-amber-600'
                  : 'text-red-600'
            }`}
          />
          {T.tracking} ({carrierName}):
        </span>
        <span
          className={`font-mono font-bold px-2 py-0.5 rounded border select-all text-xs ${
            theme.isDarkTheme
              ? 'bg-[#242c38] text-amber-300 border-[#3a4352]'
              : isUkrposhta
                ? 'bg-white text-gray-900 border-amber-200'
                : 'bg-white text-gray-900 border-red-200'
          }`}
        >
          {trackingNumber}
        </span>
      </div>

      {tracking?.status_description && (
        <div
          className={`p-2 rounded-lg border text-xs space-y-1 ${
            theme.isDarkTheme
              ? 'bg-[#242c38] border-[#3a4352]'
              : 'bg-white/90 border-slate-200/80'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-400 font-medium flex items-center gap-1">
              <Truck className="h-3.5 w-3.5 text-slate-400" />
              {T.deliveryStatus}:
            </span>
            {badge && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.bg}`}>
                {badge.text}
              </span>
            )}
          </div>
          <p className={`font-medium ${theme.isDarkTheme ? 'text-slate-100' : 'text-slate-800'}`}>
            {tracking.status_description}
          </p>
          {tracking.scheduled_delivery_date && (
            <p className="text-[11px] text-slate-400 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {T.deliveryEstimated}: {tracking.scheduled_delivery_date}
            </p>
          )}
          {tracking.warehouse_recipient && (
            <p className="text-[11px] text-slate-400 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {tracking.warehouse_recipient}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <a
          href={trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center gap-1.5 flex-1 font-medium py-1.5 px-3 rounded-lg text-xs transition-colors shadow-xs ${
            theme.isDarkTheme
              ? 'bg-[#c9a24b] text-[#17120f] font-bold hover:bg-[#d8b25c]'
              : isUkrposhta
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {trackingLabel}
        </a>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(trackingNumber)
            haptic('light')
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          }}
          className={`px-2.5 py-1.5 border rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${
            theme.isDarkTheme
              ? 'bg-[#242c38] border-[#3a4352] text-slate-200 hover:bg-[#2d3748]'
              : isUkrposhta
                ? 'bg-white border-amber-200 text-amber-800 hover:bg-amber-100'
                : 'bg-white border-red-200 text-red-700 hover:bg-red-100'
          }`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? T.copied : T.copy}
        </button>
      </div>
    </div>
  )
}

function OrderRow({
  order,
  lang,
  botUsername,
}: {
  order: Order
  lang: 'uk' | 'en' | 'ru'
  botUsername?: string
}) {
  const theme = useStoreTheme()
  const [open, setOpen] = useState(false)
  const [reorderError, setReorderError] = useState(false)
  const [isChangingPayment, setIsChangingPayment] = useState(false)
  const [addedItemName, setAddedItemName] = useState<string | null>(null)
  const [copiedSummary, setCopiedSummary] = useState(false)

  const navigate = useNavigate()
  const setCartCount = useAppStore((s) => s.setCartCount)
  const qc = useQueryClient()
  const T = getT(lang, 'orders')

  const { data: availablePaymentMethods } = useQuery<{ methods: string[] }>({
    queryKey: ['available-payment-methods'],
    queryFn: () => api.get<{ methods: string[] }>('/payment/methods'),
    staleTime: 60_000,
  })

  const methodsList =
    availablePaymentMethods?.methods?.length
      ? availablePaymentMethods.methods
      : ['liqpay', 'monopay', 'manual', 'cod']

  const deleteMutation = useMutation<unknown, ApiError>({
    mutationFn: () => api.delete(`/orders/${order.id}`),
    onSuccess: () => {
      haptic('success')
      qc.setQueryData<Order[]>(['orders'], (prev) =>
        prev ? prev.filter((o) => o.id !== order.id) : []
      )
    },
    onError: (err) => {
      haptic('error')
      const msg = err.message.includes('order_already_seen') ? T.deleteSeenError : err.message
      alert(msg)
    },
  })

  const reorderMutation = useMutation<{ added: unknown[] }, ApiError>({
    mutationFn: () => api.post(`/orders/${order.id}/reorder`, {}),
    onSuccess: async (result) => {
      if (result.added?.length) {
        haptic('success')
        const cart = await api.get<{ count: number }>('/cart')
        setCartCount(cart.count)
        qc.invalidateQueries({ queryKey: ['cart'] })
        void navigate({ to: '/cart' })
      } else {
        haptic('error')
        setReorderError(true)
        setTimeout(() => setReorderError(false), 3000)
      }
    },
    onError: () => {
      haptic('error')
      setReorderError(true)
      setTimeout(() => setReorderError(false), 3000)
    },
  })

  const addItemMutation = useMutation({
    mutationFn: async ({ productId, name }: { productId: number; name: string }) => {
      await api.post('/cart', { product_id: productId, qty: 1 })
      return name
    },
    onSuccess: async (name) => {
      haptic('success')
      setAddedItemName(name)
      const cart = await api.get<{ count: number }>('/cart')
      setCartCount(cart.count)
      qc.invalidateQueries({ queryKey: ['cart'] })
      setTimeout(() => setAddedItemName(null), 2500)
    },
    onError: () => {
      haptic('error')
    },
  })

  interface PayOrderResult {
    order_id: number
    paid: boolean
    payment_method?: string
    payment_url?: string
    total_uah: number
    message?: string
  }

  const payMutation = useMutation<PayOrderResult, ApiError>({
    mutationFn: () => api.post<PayOrderResult>(`/orders/${order.id}/pay`, {}),
    onSuccess: (res) => {
      haptic('success')
      if (res.paid) {
        qc.invalidateQueries({ queryKey: ['orders'] })
        return
      }
      if (res.payment_url) {
        openExternalLink(res.payment_url)
      }
    },
    onError: () => {
      haptic('error')
      alert(T.payFailed)
    },
  })

  const updatePaymentMethodMutation = useMutation({
    mutationFn: (newMethod: string) =>
      api.patch(`/orders/${order.id}/payment-method`, { payment_method: newMethod }),
    onSuccess: () => {
      haptic('success')
      qc.invalidateQueries({ queryKey: ['orders'] })
      setIsChangingPayment(false)
    },
    onError: () => {
      haptic('error')
      alert(T.payFailed)
    },
  })

  async function handleDelete() {
    const ok = window.confirm(T.deleteConfirm(order.id))
    if (!ok) return
    haptic('medium')
    deleteMutation.mutate()
  }

  function getPaymentMethodLabel(T: ReturnType<typeof getT<'orders'>>, method?: string): string {
    if (!method) return ''
    const dict = (T as { paymentMethods?: Record<string, string> }).paymentMethods
    return dict?.[method] ?? method
  }

  function handleCopyOrderSummary() {
    const lines = [
      `Замовлення #${order.id} (${T.status[order.status] ?? order.status})`,
      `Сума: ${order.total_uah} UAH`,
      `Статус оплати: ${order.paid ? T.paid : T.unpaid}`,
      order.payment_method ? `Спосіб оплати: ${getPaymentMethodLabel(T, order.payment_method)}` : '',
      order.delivery ? `Доставка: ${order.delivery}` : '',
      order.tracking_number ? `ТТН: ${order.tracking_number}` : '',
      'Товари:',
      ...order.items.map((i) => ` • ${i.name} × ${i.qty} — ${i.line_uah} UAH`),
    ]
      .filter(Boolean)
      .join('\n')

    void navigator.clipboard.writeText(lines)
    haptic('light')
    setCopiedSummary(true)
    setTimeout(() => setCopiedSummary(false), 2000)
  }

  function handleOpenSupport() {
    haptic('light')
    if (botUsername) {
      openExternalLink(`https://t.me/${botUsername}?start=order_${order.id}`)
    } else {
      handleCopyOrderSummary()
    }
  }

  const isOnlinePayment =
    order.payment_method === 'liqpay' ||
    order.payment_method === 'monopay' ||
    order.payment_method === 'stripe' ||
    order.payment_method === 'sandbox' ||
    order.payment_method === 'telegram'

  return (
    <StorefrontCard className="mb-3 overflow-hidden">
      {/* Row header */}
      <button
        type="button"
        className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors ${
          theme.isDarkTheme ? 'hover:bg-[#2d3748]/50' : 'hover:bg-slate-50/50'
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-lg">{STATUS_ICON[order.status] ?? '📋'}</span>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>
            #{order.id} — {T.status[order.status] ?? order.status}
          </p>
          <p className="text-xs text-slate-400">{formatDate(order.created_at, lang)}</p>
        </div>
        <span
          className={`font-bold text-sm text-right shrink-0 ${
            theme.isDarkTheme ? 'text-amber-300' : 'text-slate-900'
          }`}
        >
          {order.total_uah} UAH
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        )}
      </button>

      {/* Expanded detail */}
      {open && (
        <div
          className={`px-4 pb-4 pt-2 space-y-3 border-t ${
            theme.isDarkTheme
              ? 'bg-[#1a202c]/70 border-[#2d3748]'
              : 'bg-slate-50/60 border-slate-100'
          }`}
        >
          {/* Order Progress Timeline */}
          <OrderTimeline status={order.status} lang={lang} />

          {/* Payment block */}
          {order.paid ? (
            <div
              className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                theme.isDarkTheme
                  ? 'bg-emerald-950/30 border border-emerald-800 text-emerald-300'
                  : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              }`}
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span>
                {T.paid} — {getPaymentMethodLabel(T, order.payment_method) || 'Онлайн'}
              </span>
            </div>
          ) : order.status !== 'cancelled' && order.status !== 'refunded' ? (
            <div
              className={`rounded-2xl border p-3 text-xs space-y-2.5 ${
                theme.isDarkTheme
                  ? 'border-amber-500/30 bg-amber-500/5 text-slate-200'
                  : 'border-amber-200 bg-amber-50/60'
              }`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span
                  className={`font-bold flex items-center gap-1.5 ${
                    theme.isDarkTheme ? 'text-amber-300' : 'text-amber-900'
                  }`}
                >
                  <CreditCard className={`h-4 w-4 ${theme.isDarkTheme ? 'text-amber-400' : 'text-amber-700'}`} />
                  {T.unpaid}: {order.total_uah} UAH
                </span>
                <span className="text-[11px] font-medium text-slate-400">
                  {getPaymentMethodLabel(T, order.payment_method || 'manual') || T.paymentMethod}
                </span>
              </div>

              {/* Action: Pay online / Pay buttons */}
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {isOnlinePayment ? (
                  <button
                    type="button"
                    disabled={payMutation.isPending}
                    onClick={() => {
                      haptic('medium')
                      payMutation.mutate()
                    }}
                    className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold shadow-xs transition flex-1 disabled:opacity-50 ${
                      theme.isDarkTheme
                        ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d8b25c]'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                  >
                    <CreditCard className="h-4 w-4" />
                    {payMutation.isPending ? T.paying : T.payOnlineAmount(order.total_uah)}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={payMutation.isPending}
                    onClick={() => {
                      haptic('medium')
                      payMutation.mutate()
                    }}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl font-semibold text-xs shadow-xs transition disabled:opacity-50 ${
                      theme.isDarkTheme
                        ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d8b25c]'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    {payMutation.isPending ? T.paying : T.payOnline}
                  </button>
                )}

                {order.status === 'new' && (
                  <button
                    type="button"
                    onClick={() => setIsChangingPayment((v) => !v)}
                    className={`px-3 py-1.5 rounded-xl border font-semibold text-xs transition ${
                      theme.isDarkTheme
                        ? 'border-[#3a4352] bg-[#242c38] text-slate-300 hover:bg-[#2d3748]'
                        : 'border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs'
                    }`}
                  >
                    {isChangingPayment ? '✕ Закрити' : `⚙️ ${T.changePayment ?? 'Змінити спосіб'}`}
                  </button>
                )}
              </div>

              {/* Changing payment method selector */}
              {isChangingPayment && (
                <div
                  className={`mt-2 pt-2 border-t space-y-1.5 ${
                    theme.isDarkTheme ? 'border-[#3a4352]' : 'border-amber-200/80'
                  }`}
                >
                  <p className="text-[11px] font-semibold text-slate-300">
                    {T.changePayment ?? 'Оберіть зручний спосіб оплати'}:
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {methodsList.map((m) => {
                      const isCurrent = (order.payment_method || 'manual') === m
                      const label = getPaymentMethodLabel(T, m) || m
                      return (
                        <button
                          key={m}
                          type="button"
                          disabled={updatePaymentMethodMutation.isPending || isCurrent}
                          onClick={() => {
                            haptic('medium')
                            updatePaymentMethodMutation.mutate(m)
                          }}
                          className={`flex items-center justify-between px-3 py-2 rounded-xl text-left text-xs transition border ${
                            isCurrent
                              ? theme.isDarkTheme
                                ? 'bg-amber-500/20 border-amber-500/40 font-bold text-amber-300'
                                : 'bg-amber-100 border-amber-300 font-bold text-amber-950'
                              : theme.isDarkTheme
                                ? 'bg-[#242c38] border-[#3a4352] text-slate-300 hover:bg-[#2d3748]'
                                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <span>{label}</span>
                          {isCurrent && <Check className="h-4 w-4 text-amber-500 shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* IBAN requisites if manual/IBAN */}
              {(order.payment_method === 'manual' || !order.payment_method) && (
                <IbanPaymentCard orderId={order.id} />
              )}
            </div>
          ) : null}

          {/* Tracking */}
          {order.tracking_number && (
            <DeliveryTrackingCard
              orderId={order.id}
              trackingNumber={order.tracking_number}
              deliveryMethod={order.delivery}
              lang={lang}
            />
          )}

          {/* Delivery Info */}
          {(order.delivery || order.city) && (
            <div
              className={`rounded-xl border p-2.5 text-xs space-y-1 ${
                theme.isDarkTheme
                  ? 'bg-[#242c38] border-[#2d3748] text-slate-300'
                  : 'bg-white border-slate-200/80 text-slate-600'
              }`}
            >
              {order.delivery && (
                <p className={`flex items-center gap-1.5 font-medium ${theme.isDarkTheme ? 'text-slate-200' : 'text-slate-700'}`}>
                  <Truck className="h-3.5 w-3.5 text-slate-400" />
                  {order.delivery}
                </p>
              )}
              {order.city && (
                <p className="flex items-center gap-1.5 text-slate-400">
                  <MapPin className="h-3.5 w-3.5 text-slate-400" />
                  {order.city}
                </p>
              )}
            </div>
          )}

          {/* Items List with Interactive Product Links & Quick Add */}
          {order.items.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Товари в замовленні
                </span>
                {addedItemName && (
                  <span className="text-[11px] font-semibold text-emerald-500 animate-fadeIn">
                    ✓ {T.itemAddedToCart ?? 'Додано в кошик'}
                  </span>
                )}
              </div>
              <ul
                className={`divide-y border rounded-2xl overflow-hidden shadow-2xs ${
                  theme.isDarkTheme
                    ? 'divide-[#2d3748] border-[#2d3748] bg-[#242c38]'
                    : 'divide-slate-100 border-slate-200/80 bg-white'
                }`}
              >
                {order.items.map((item, i) => (
                  <li key={i} className="flex items-center justify-between p-3 text-xs gap-2">
                    <div className="min-w-0 flex-1">
                      {item.product_id ? (
                        <button
                          type="button"
                          onClick={() => {
                            haptic('light')
                            void navigate({
                              to: '/product/$productSlug',
                              params: { productSlug: String(item.product_id) },
                            })
                          }}
                          className={`font-medium text-left transition flex items-center gap-1 group ${
                            theme.isDarkTheme
                              ? 'text-[#f8f3ea] hover:text-amber-400'
                              : 'text-slate-900 hover:text-brand-600'
                          }`}
                        >
                          <span className="truncate group-hover:underline">{item.name}</span>
                          <ExternalLink className="h-3 w-3 text-slate-400 group-hover:text-amber-400 shrink-0" />
                        </button>
                      ) : (
                        <span className={`font-medium truncate block ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>
                          {item.name}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 font-sans block">
                        {item.qty} шт. × {Math.round(item.line_uah / item.qty)} UAH
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`font-bold text-xs ${
                          theme.isDarkTheme ? 'text-amber-300' : 'text-slate-900'
                        }`}
                      >
                        {item.line_uah} UAH
                      </span>
                      {item.product_id && (
                        <button
                          type="button"
                          title={T.addMore ?? 'Додати в кошик'}
                          disabled={addItemMutation.isPending}
                          onClick={() => {
                            haptic('medium')
                            addItemMutation.mutate({ productId: item.product_id!, name: item.name })
                          }}
                          className={`p-1.5 rounded-lg border transition shadow-2xs ${
                            theme.isDarkTheme
                              ? 'border-[#3a4352] bg-[#1a202c] hover:bg-[#2d3748] text-slate-200'
                              : 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700'
                          }`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quick Actions Footer */}
          <div
            className={`flex items-center justify-between flex-wrap gap-2 pt-2 border-t ${
              theme.isDarkTheme ? 'border-[#2d3748]' : 'border-slate-200/60'
            }`}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                disabled={reorderMutation.isPending}
                onClick={() => {
                  haptic('medium')
                  reorderMutation.mutate()
                }}
                className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold shadow-2xs transition ${
                  theme.isDarkTheme
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
                    : 'border-brand-200 bg-brand-50 hover:bg-brand-100 text-brand-700'
                }`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {reorderMutation.isPending
                  ? T.reordering
                  : reorderError
                    ? T.reorderFail
                    : T.buyAgain ?? T.reorder}
              </button>

              <button
                type="button"
                onClick={handleOpenSupport}
                className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium shadow-2xs transition ${
                  theme.isDarkTheme
                    ? 'border-[#2d3748] bg-[#242c38] hover:bg-[#2d3748] text-slate-200'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <MessageCircle className="h-3.5 w-3.5 text-slate-400" />
                {T.orderSupport ?? 'Підтримка'}
              </button>

              <button
                type="button"
                onClick={handleCopyOrderSummary}
                className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium shadow-2xs transition ${
                  theme.isDarkTheme
                    ? 'border-[#2d3748] bg-[#242c38] hover:bg-[#2d3748] text-slate-200'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                {copiedSummary ? (
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <Share2 className="h-3.5 w-3.5 text-slate-400" />
                )}
                {copiedSummary ? T.copied : T.copyOrderDetails ?? 'Скопіювати'}
              </button>

              {order.status === 'new' && (
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={handleDelete}
                  className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium shadow-2xs transition ${
                    theme.isDarkTheme
                      ? 'border-rose-900/50 bg-rose-950/20 hover:bg-rose-950/40 text-rose-400'
                      : 'border-rose-200 bg-white hover:bg-rose-50 text-rose-600'
                  }`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {T.delete}
                </button>
              )}
            </div>

            <span className={`font-bold text-sm ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>
              {T.total}: {order.total_uah} UAH
            </span>
          </div>
        </div>
      )}
    </StorefrontCard>
  )
}

export function OrdersPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('orders')

  const { data: settings } = useQuery<{ shop_name?: string; bot_username?: string }>({
    queryKey: ['public-settings'],
    queryFn: () => api.get('/settings'),
    staleTime: 300_000,
  })
  const isLodging = isLodgingShop(settings?.shop_name)

  const { data: orders, isLoading } = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => api.get<Order[]>('/orders'),
  })
  const { data: lodgingBookings, isLoading: loadingBookings } = useQuery<LodgingBooking[]>({
    queryKey: ['lodging-my-bookings'],
    queryFn: () => api.get<LodgingBooking[]>('/lodging/bookings'),
    enabled: isLodging,
  })

  if (isLoading || (isLodging && loadingBookings)) {
    return <StorefrontLoading message={T.loading} />
  }
  if (!orders?.length && !lodgingBookings?.length) {
    return <StorefrontEmptyState icon={Package} title={T.empty} />
  }

  return (
    <StorefrontPage title={T.title} badge={orders?.length}>
      {Boolean(lodgingBookings?.length) && (
        <LodgingBookingsSection bookings={lodgingBookings!} lang={lang} />
      )}
      {Boolean(orders?.length) && (
        <div className="space-y-3">
          {orders!.map((o) => (
            <OrderRow key={o.id} order={o} lang={lang} botUsername={settings?.bot_username} />
          ))}
        </div>
      )}
    </StorefrontPage>
  )
}
